'use client'

import * as React from 'react'
import MUIBox from '@mui/material/Box'
import MUICollapse from '@mui/material/Collapse'
import MUIPaper from '@mui/material/Paper'
import MUITypography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'

type RenderProps<T> = {
  selectedRows: T[]
  selectedCount: number
}

type RenderNode<T> = React.ReactNode | ((props: RenderProps<T>) => React.ReactNode)

export type DataGridSelectionTrayProps<T> = {
  selectedRows: T[]
  height?: number
  bottomOffset?: number
  left?: RenderNode<T>
  children?: RenderNode<T>
  sx?: SxProps<Theme>
  paperSx?: SxProps<Theme>
}

export const DataGridSelectionTray = <T,>({
  selectedRows = [] as T[],
  height = 72,
  left,
  children,
  sx
}: DataGridSelectionTrayProps<T>) => {
  const open = selectedRows.length > 0

  const render = (node: RenderNode<T> | undefined) => {
    if (!node) return null
    return typeof node === 'function' ? node({ selectedRows, selectedCount: selectedRows.length }) : node
  }

  return (
    <MUIBox data-testid="DataGridSelectionTray">
    <MUICollapse in={open} timeout={200} unmountOnExit>
      <MUIPaper
        elevation={10}
        sx={[
          {
            height,
            width: '100%',
            borderRadius: 0, // full-width bar vibe
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            gap: 2
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        <MUIBox sx={{ display: 'flex', alignItems: 'center', minWidth: 160 }}>
          {left ? (
            render(left)
          ) : (
            <MUITypography variant="body2" fontWeight={700}>
              {selectedRows.length} selected
            </MUITypography>
          )}
        </MUIBox>

        <MUIBox sx={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: '100% / 3' }}>{render(children)}</MUIBox>
      </MUIPaper>
    </MUICollapse>
    </MUIBox>
  )
}
